import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Btn, Field, Input, Textarea, Toggle, Modal, Confirm, Empty } from "@/pages/admin/managers/ui";
import { Plus, Pencil, Trash2 } from "lucide-react";

export default function ServicesManager() {
  const [items, setItems] = useState([]);
  const [modal, setModal] = useState(null);
  const [confirmId, setConfirmId] = useState(null);

  const load = async () => setItems((await api.get("/admin/services")).data);
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!modal.name?.trim()) return toast.error("El nombre es obligatorio.");
    try {
      const body = {
        name: modal.name, description: modal.description || "", starting_price: modal.starting_price || "",
        image_url: modal.image_url || "", is_visible: modal.is_visible !== false,
      };
      if (modal.id) await api.put(`/admin/services/${modal.id}`, body);
      else await api.post("/admin/services", body);
      toast.success("Servicio guardado.");
      setModal(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async () => {
    await api.delete(`/admin/services/${confirmId}`);
    toast.success("Servicio eliminado."); setConfirmId(null); load();
  };

  return (
    <div>
      <div className="flex justify-end mb-6">
        <Btn data-testid="add-service-btn" onClick={() => setModal({ is_visible: true })}><Plus size={16} className="inline mr-1" /> Nuevo servicio</Btn>
      </div>
      {items.length === 0 ? <Empty>No hay servicios.</Empty> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((s) => (
            <div key={s.id} data-testid={`service-row-${s.id}`} className="bg-[#18181B] border border-[#27272A] rounded-lg overflow-hidden">
              {s.image_url && <img src={s.image_url} alt={s.name} className="w-full h-32 object-cover" />}
              <div className="p-4">
                <p className="text-white font-medium">{s.name} {!s.is_visible && <span className="text-xs text-[#8C8375]">(oculto)</span>}</p>
                <p className="text-xs text-[#8C8375] mt-1 line-clamp-2">{s.description}</p>
                <p className="text-xs text-[#C5A059] mt-2 font-mono">{s.starting_price ? `Desde ${s.starting_price}` : "Consultar precio"}</p>
                <div className="flex gap-2 mt-3">
                  <Btn variant="subtle" data-testid={`service-edit-${s.id}`} onClick={() => setModal(s)}><Pencil size={14} /></Btn>
                  <Btn variant="danger" data-testid={`service-delete-${s.id}`} onClick={() => setConfirmId(s.id)}><Trash2 size={14} /></Btn>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <Modal open={!!modal} onClose={() => setModal(null)} title={modal?.id ? "Editar servicio" : "Nuevo servicio"} testid="service-modal">
        {modal && (
          <>
            <Field label="Nombre"><Input data-testid="service-name-input" value={modal.name || ""} onChange={(e) => setModal({ ...modal, name: e.target.value })} /></Field>
            <Field label="Descripción"><Textarea rows={3} value={modal.description || ""} onChange={(e) => setModal({ ...modal, description: e.target.value })} /></Field>
            <Field label="Precio inicial (opcional)"><Input value={modal.starting_price || ""} onChange={(e) => setModal({ ...modal, starting_price: e.target.value })} placeholder="Dejar vacío para 'Consultar precio'" /></Field>
            <Field label="URL de imagen"><Input value={modal.image_url || ""} onChange={(e) => setModal({ ...modal, image_url: e.target.value })} placeholder="https://…" /></Field>
            <Toggle testid="service-visible-toggle" checked={modal.is_visible !== false} onChange={(v) => setModal({ ...modal, is_visible: v })} label="Visible en el sitio" />
            <div className="flex justify-end gap-3 pt-2">
              <Btn variant="ghost" onClick={() => setModal(null)}>Cancelar</Btn>
              <Btn data-testid="service-save-btn" onClick={save}>Guardar</Btn>
            </div>
          </>
        )}
      </Modal>
      <Confirm open={!!confirmId} onClose={() => setConfirmId(null)} onConfirm={del} message="¿Eliminar este servicio?" />
    </div>
  );
}
